// src/Components/Modules/HR/DocumentViewer.js
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip,
  Button, Box, Typography, CircularProgress, Chip, Stack,
} from "@mui/material";
import {
  FaTimes, FaDownload, FaExpand, FaCompress, FaFileAlt, FaSearchPlus,
  FaSearchMinus, FaUndo, FaImage, FaFilePdf,
} from "react-icons/fa";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";

const DocumentViewer = ({ open, doc, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [url, setUrl] = useState(null);
  const [kind, setKind] = useState("other"); // pdf | image | other
  const [fileName, setFileName] = useState("document");
  const [fullScreen, setFullScreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const urlRef = useRef(null);

  // Detect file kind from MIME type or extension
  const detectKind = (document, blob) => {
    const name = document?.file_path || document?.file_name || "";
    const ext = name.includes(".") ? name.split(".").pop().toLowerCase() : "";
    const mime = blob?.type || "";
    if (mime.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "bmp"].includes(ext)) return "image";
    if (mime === "application/pdf" || ext === "pdf") return "pdf";
    return "other";
  };

  const load = useCallback(async () => {
    if (!doc?.id) return;
    setLoading(true);
    setError(null);
    setZoom(1);
    try {
      const res = await AxiosAuthInstance.get(`hr/documents/${doc.id}/download`, {
        responseType: "blob",
      });
      const objectUrl = URL.createObjectURL(res.data);
      if (urlRef.current) URL.revokeObjectURL(urlRef.current); // prevent memory leaks
      urlRef.current = objectUrl;
      setUrl(objectUrl);
      setKind(detectKind(doc, res.data));
      setFileName(doc.file_path ? doc.file_path.split("/").pop() : doc.caption || "document");
    } catch (err) {
      console.error("Viewer load error:", err);
      setError("Failed to load the document preview.");
    } finally {
      setLoading(false);
    }
  }, [doc]);

  useEffect(() => {
    if (open && doc) load();
  }, [open, doc, load]);

  // Revoke object URL on unmount
  useEffect(() => () => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  const handleClose = () => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    setUrl(null);
    setFullScreen(false);
    onClose();
  };

  // Download straight from the already-loaded blob (no second request)
  const handleDownload = () => {
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const kindMeta = {
    pdf: { icon: <FaFilePdf />, label: "PDF", color: "#dc2626", bg: "#fef2f2" },
    image: { icon: <FaImage />, label: "Image", color: "#16a34a", bg: "#f0fdf4" },
    other: { icon: <FaFileAlt />, label: "File", color: "#475569", bg: "#f1f5f9" },
  }[kind];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      fullScreen={fullScreen}
      PaperProps={{ sx: { height: fullScreen ? "100%" : "90vh", borderRadius: fullScreen ? 0 : 2 } }}
    >
      {/* Header */}
      <DialogTitle sx={{ py: 1.5, borderBottom: "1px solid #e2e8f0" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 1, bgcolor: kindMeta.bg, color: kindMeta.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
            {kindMeta.icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} color="#1e293b" noWrap>{fileName}</Typography>
            <Typography variant="caption" color="#64748b">
              {doc?.employee?.employee_name ? `${doc.employee.employee_name} • ` : ""}
              {doc?.type ? doc.type.toUpperCase() : ""}
            </Typography>
          </Box>
          <Chip label={kindMeta.label} size="small" sx={{ bgcolor: kindMeta.bg, color: kindMeta.color, fontWeight: 700 }} />

          {/* Zoom controls (images only) */}
          {kind === "image" && url && (
            <Stack direction="row" spacing={0.5} sx={{ mx: 1 }}>
              <Tooltip title="Zoom out">
                <span><IconButton size="small" disabled={zoom <= 0.5} onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}><FaSearchMinus /></IconButton></span>
              </Tooltip>
              <Tooltip title="Reset zoom">
                <span><IconButton size="small" disabled={zoom === 1} onClick={() => setZoom(1)}><FaUndo /></IconButton></span>
              </Tooltip>
              <Tooltip title="Zoom in">
                <span><IconButton size="small" disabled={zoom >= 3} onClick={() => setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))}><FaSearchPlus /></IconButton></span>
              </Tooltip>
            </Stack>
          )}

          <Tooltip title={fullScreen ? "Exit full screen" : "Full screen"}>
            <IconButton onClick={() => setFullScreen((f) => !f)}>{fullScreen ? <FaCompress /> : <FaExpand />}</IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton onClick={handleClose}><FaTimes /></IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      {/* Body */}
      <DialogContent
        sx={{
          p: 0, m: 0,
          height: fullScreen ? "calc(100vh - 118px)" : "62vh",
          bgcolor: kind === "image" ? "#0f172a" : "#f8fafc",
          overflow: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <CircularProgress sx={{ color: "#6366f1" }} />
            <Typography variant="body2" color="#64748b">Loading secure preview…</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: "center", p: 4 }}>
            <FaFileAlt style={{ fontSize: 48, color: "#cbd5e1", marginBottom: 16 }} />
            <Typography variant="h6" color="#475569" gutterBottom>Preview unavailable</Typography>
            <Typography variant="body2" color="#64748b" sx={{ mb: 2 }}>{error}</Typography>
            <Button variant="contained" onClick={load} sx={{ textTransform: "none", fontWeight: 600 }}>Try Again</Button>
          </Box>
        ) : url && kind === "pdf" ? (
          <iframe title={fileName} src={url} style={{ width: "100%", height: "100%", border: "none" }} />
        ) : url && kind === "image" ? (
          <Box sx={{ width: "100%", height: "100%", overflow: "auto", display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
            <Box
              component="img"
              src={url}
              alt={fileName}
              sx={{
                maxWidth: "100%", maxHeight: "100%", objectFit: "contain",
                transform: `scale(${zoom})`, transition: "transform 0.2s ease",
                boxShadow: "0 10px 40px rgba(0,0,0,0.5)", borderRadius: 1,
              }}
            />
          </Box>
        ) : (
          <Box sx={{ textAlign: "center", p: 4 }}>
            <FaFileAlt style={{ fontSize: 48, color: "#cbd5e1", marginBottom: 16 }} />
            <Typography variant="h6" color="#475569" gutterBottom>Preview not supported for this file type</Typography>
            <Typography variant="body2" color="#64748b" sx={{ mb: 2 }}>Office/Word files can be viewed after downloading.</Typography>
            <Button variant="contained" startIcon={<FaDownload />} onClick={handleDownload} sx={{ textTransform: "none", fontWeight: 600 }}>
              Download File
            </Button>
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 2, py: 1, borderTop: "1px solid #e2e8f0" }}>
        <Typography variant="caption" color="#94a3b8" sx={{ mr: "auto" }}>
          {kind === "image" && url ? `Zoom: ${Math.round(zoom * 100)}%` : "Secured preview — access is permission-controlled"}
        </Typography>
        <Button onClick={handleClose} sx={{ textTransform: "none", fontWeight: 600 }}>Close</Button>
        <Button
          variant="contained"
          startIcon={<FaDownload />}
          onClick={handleDownload}
          disabled={!url}
          sx={{ textTransform: "none", fontWeight: 600, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentViewer;